import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Form, Input, Button, Select, Radio, App } from "antd";

import { getBranchList } from "../../../general/api/branch.api";
import { createAccount } from "../api/registerAPI";
import { styles } from "../styles/registerStyles";
import { RegisterSchema, IRegister } from "../type/registerType";

export default function RegisterForm() {
  const navigate = useNavigate();
  const { message } = App.useApp();

  const { data: branchData } = useQuery({
    queryKey: ["branches"],
    queryFn: getBranchList,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const branchOptions =
    branchData?.results.map((branch: { id: string; branchName: string }) => ({
      value: branch.id,
      label: branch.branchName,
    })) || [];

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IRegister>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      role: "client",
    },
  });

  const onSubmit = async (data: IRegister) => {
    try {
      await createAccount(data);
      message.success("Registration successful! Please wait for approval.");
      navigate("/login");
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const role = useWatch({
    control,
    name: "role",
  });

  return (
    <Form
      name="registerForm"
      onFinish={handleSubmit(onSubmit)}
      labelAlign="left"
      colon={false}
      layout="vertical"
    >
      <Form.Item
        label="Name"
        name="name"
        hasFeedback
        validateStatus={errors.name ? "error" : ""}
        help={errors.name?.message}
        required
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              placeholder="Please enter your name"
              {...field}
              size="large"
            />
          )}
        />
      </Form.Item>

      <Form.Item
        label="IC Number"
        name="icNumber"
        hasFeedback
        validateStatus={errors.icNumber ? "error" : ""}
        help={errors.icNumber?.message}
        tooltip="Example: 900101012345"
        required
      >
        <Controller
          name="icNumber"
          control={control}
          render={({ field }) => (
            <Input
              placeholder="Please enter your IC number"
              {...field}
              size="large"
            />
          )}
        />
      </Form.Item>

      {/* <Form.Item
        label="Address"
        name="address"
        hasFeedback
        validateStatus={errors.address ? "error" : ""}
        help={errors.address?.message}
      >
        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <Input
              placeholder="Please enter your address"
              {...field}
              size="large"
            />
          )}
        />
      </Form.Item> */}

      <Form.Item
        label="Phone Number"
        name="phoneNumber"
        hasFeedback
        validateStatus={errors.phoneNumber ? "error" : ""}
        help={errors.phoneNumber?.message}
        required
      >
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <Input
              placeholder="Please enter your phone number"
              {...field}
              size="large"
            />
          )}
        />
      </Form.Item>

      <Form.Item
        label="Email"
        name="email"
        hasFeedback
        validateStatus={errors.email ? "error" : ""}
        help={errors.email?.message}
        required
      >
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              placeholder="Please enter your email"
              {...field}
              size="large"
            />
          )}
        />
      </Form.Item>

      <Form.Item
        label="Role"
        name="role"
        hasFeedback
        validateStatus={errors.role ? "error" : ""}
        help={errors.role?.message}
      >
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <Radio.Group {...field} size="small">
              <Radio value="client">Client</Radio>
              <Radio value="agent">Agent</Radio>
            </Radio.Group>
          )}
        />
      </Form.Item>

      {role === "agent" && (
        <>
          <Form.Item
            label="Branch"
            name="branchId"
            hasFeedback
            validateStatus={errors.branchId ? "error" : ""}
            help={errors.branchId?.message}
            required={role === "agent"}
          >
            <Controller
              name="branchId"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Select Branch"
                  {...field}
                  size="large"
                  options={branchOptions}
                  allowClear
                />
              )}
            />
          </Form.Item>

          <Form.Item
            label="Agency Name"
            name="agency"
            hasFeedback
            validateStatus={errors.agency ? "error" : ""}
            help={errors.agency?.message}
            required={role === "agent"}
          >
            <Controller
              name="agency"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Please enter your agency name"
                  {...field}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </>
      )}

      <Form.Item
        label="Password"
        name="password"
        hasFeedback
        validateStatus={errors.password ? "error" : ""}
        help={errors.password?.message}
        required
      >
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <Input.Password
              placeholder="Please enter your password"
              {...field}
              size="large"
            />
          )}
        />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          style={styles.registerButton}
          size="large"
        >
          Register
        </Button>
      </Form.Item>
    </Form>
  );
}
